import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { PhraseRef, PuzzleType, Role } from '@il-colpo/engine';
import { Button } from '../../ui/index.ts';
import { useGame } from '../GameContext.tsx';
import styles from './puzzles.module.css';

export interface PuzzleProps {
  puzzleId: string;
  type: PuzzleType;
  /** The role-filtered view of this puzzle. */
  v: Record<string, unknown>;
  /** A teammate's card in training: only what that seat knows, nothing to operate. */
  lens?: boolean;
}

/** Who works a puzzle with their hands (GDD §8); `null` means everybody. */
export function solverRole(type: PuzzleType, controller?: Role): Role | null {
  switch (type) {
    case 'lock':
    case 'safe':
    case 'laser':
      return 'locksmith';
    case 'panel':
    case 'cameras':
    case 'password':
      return 'hacker';
    case 'escape':
      return controller ?? 'hacker';
    case 'corridor':
    case 'vault':
      return null;
  }
}

/** A line of a clue with a button that says it to the team. */
export function ClueLine({
  phrase,
  extra,
  highlight = false,
}: {
  phrase: PhraseRef;
  extra?: ReactNode;
  highlight?: boolean;
}) {
  const { t } = useTranslation('game');
  const { sentence, say } = useGame();
  return (
    <li className={[styles['clue'], highlight ? styles['next'] : ''].join(' ')}>
      <span className={styles['clueText']}>{sentence(phrase)}</span>
      {extra}
      <Button size="md" variant="ghost" icon="chat" onClick={() => say(phrase)}>
        {t('puzzle.say')}
      </Button>
    </li>
  );
}

/** What the team has said about this puzzle, in order (phrases whose id is in `ids`). */
export function useHeard(
  puzzleId: string,
  ids: readonly string[],
): { who: string; phrase: PhraseRef; id: number }[] {
  const { view } = useGame();
  return useMemo(() => {
    const start = view.events.findIndex(
      (e) => e.type === 'puzzle.created' && e.data['puzzleId'] === puzzleId,
    );
    const out: { who: string; phrase: PhraseRef; id: number }[] = [];
    for (const ev of view.events.slice(start < 0 ? 0 : start)) {
      if (ev.type !== 'chat') continue;
      const phrase = ev.data['phrase'] as PhraseRef;
      if (!ids.includes(phrase.id)) continue;
      const by = view.players.find((p) => p.id === ev.data['by']);
      out.push({ who: by?.name ?? '?', phrase, id: ev.id });
    }
    return out;
  }, [view.events, view.players, puzzleId, ids]);
}

/** "What you were told": a list of the phrases heard about this puzzle. */
export function HeardList({ puzzleId, ids }: { puzzleId: string; ids: readonly string[] }) {
  const { t } = useTranslation('game');
  const { sentence } = useGame();
  const heard = useHeard(puzzleId, ids);
  return (
    <section className={styles['heard']} aria-label={t('puzzle.heard')}>
      <h4 className={styles['heardTitle']}>{t('puzzle.heard')}</h4>
      {heard.length === 0 ? (
        <p className={styles['muted']}>{t('puzzle.heardNone')}</p>
      ) : (
        <ul className={styles['heardList']}>
          {heard.map((h) => (
            <li key={h.id}>
              <strong>{`${h.who}:`}</strong> {sentence(h.phrase)}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** The latest private event of a type for this puzzle (e.g. "the pin fell"), if recent. */
export function useLatestEvent(
  puzzleId: string,
  type: string,
  withinMs = 6000,
): { data: Record<string, unknown>; atMs: number } | null {
  const { view } = useGame();
  for (let i = view.events.length - 1; i >= 0; i--) {
    const ev = view.events[i];
    if (!ev) continue;
    if (ev.type === type && ev.data['puzzleId'] === puzzleId) {
      return view.game.clockMs - ev.atMs <= withinMs ? { data: ev.data, atMs: ev.atMs } : null;
    }
  }
  return null;
}

/** Shown to roles that have nothing to do with a puzzle. */
export function Bystander({ type }: { type: PuzzleType }) {
  const { t } = useTranslation('game');
  const { g } = useGame();
  return (
    <p className={styles['muted']}>
      {t('puzzle.bystander', {
        puzzle: g(`puzzles.${type}.name`),
        role: g(`roles.${solverRole(type) ?? 'mastermind'}`),
      })}
    </p>
  );
}
